import { Cart, ComboItems, Size } from "../../types";
import { Action } from "../../types/redux";

export interface CartState {
  isSaving: boolean;
  cart: Cart;
  loading: boolean;
  loaded: boolean;
  showDistributorPrices: boolean;
  shippingFormStates: {
    isFormOpenedAndValid: boolean;
    hasCheckedSaveAndContinue: boolean;
  };
  billingFormStates: {
    isFormOpenedAndValid: boolean;
    hasCheckedSaveAndContinue: boolean;
  };
}

export interface CartPayload {
  id: string;
  size: Size;
  amount: number;
  isSaving: boolean;
  cart: Cart;
  showDistributorPrices: boolean;
  isShippingFormOpenedAndValid: boolean;
  hasCheckedSaveAndContinue: boolean;
  isBillingFormOpenedAndValid: boolean;
}

export enum CART_ACTION_ENUM {
  UPDATE_CART = "update_cart",
  UPDATE_ITEM_SIZES = "update_item_sizes",
  SET_SAVING = "set_saving",
  SET_SHOW_DISTRIBUTOR_PRICES = "set_show_distributor_prices",
  SET_SHIPPING_FORM_STATES = "set_shipping_form_states",
  SET_BILLING_FORM_STATES = "set_billing_form_states",
  UPDATE_LOADING = "update_cart_loading",
  RESET_STATE = "reset_state",
}

const initialState: CartState = {
  isSaving: false,
  cart: {
    comboItems: [] as ComboItems[],
    currency: "cad",
    shippingTotal: 0,
    productsTotal: 0,
    orderTotal: 0,
    carbonNeutralityFeeTotal: 0,
    pricingRestrictionsAreMet: true,
    carbonNeutral: false,
    allItemsAreBelowMaximumQuantity: true,
  },
  showDistributorPrices: false,
  shippingFormStates: {
    isFormOpenedAndValid: false,
    hasCheckedSaveAndContinue: false,
  },
  loading: false,
  loaded: false,
  billingFormStates: {
    isFormOpenedAndValid: false,
    hasCheckedSaveAndContinue: false,
  },
};

const reducer = (
  state = initialState,
  action: Action<CART_ACTION_ENUM, CartPayload>
) => {
  switch (action.type) {
    case CART_ACTION_ENUM.UPDATE_CART:
      return {
        ...state,
        cart: action.payload.cart,
        loaded: true,
        loading: false,
      };
    case CART_ACTION_ENUM.UPDATE_LOADING:
      return { ...state, loading: true, loaded: false };

    case CART_ACTION_ENUM.UPDATE_ITEM_SIZES: {
      const { id, size, amount } = action.payload;
      return {
        ...state,
        cart: {
          ...state.cart,
          comboItems: state.cart.comboItems.map((comboItem) => ({
            ...comboItem,
            items: comboItem.items.map((item) =>
              item.cartItemId === id
                ? {
                    ...item,
                    sizes: item.sizes.map((s) =>
                      s.name === size.name ? { ...s, qty: amount } : s
                    ),
                  }
                : item
            ),
          })),
        },
      };
    }
    case CART_ACTION_ENUM.SET_SAVING:
      return { ...state, isSaving: action.payload.isSaving };
    case CART_ACTION_ENUM.SET_SHOW_DISTRIBUTOR_PRICES:
      return {
        ...state,
        showDistributorPrices: action.payload.showDistributorPrices,
      };
    case CART_ACTION_ENUM.SET_SHIPPING_FORM_STATES:
      return {
        ...state,
        shippingFormStates: {
          isFormOpenedAndValid: action.payload.isShippingFormOpenedAndValid,
          hasCheckedSaveAndContinue: action.payload.hasCheckedSaveAndContinue,
        },
      };
    case CART_ACTION_ENUM.SET_BILLING_FORM_STATES:
      return {
        ...state,
        billingFormStates: {
          isFormOpenedAndValid: action.payload.isBillingFormOpenedAndValid,
          hasCheckedSaveAndContinue: action.payload.hasCheckedSaveAndContinue,
        },
      };
    case CART_ACTION_ENUM.RESET_STATE:
      return initialState;
    default:
      return state;
  }
};

export default reducer;
